import isEqual from 'lodash/isEqual';
import { ReportSection } from './report-section.enum';
import { ReportType } from './report-type.enum';
import { ReportViewModelSection } from './report-view-model-section.enum';

export const TEMPLATE_LIBRARY_FEATURE_KEY = 'reporting-template-library';
export const TEMPLATE_DESIGNER_FEATURE_KEY = 'reporting-template-designer';

export enum ReportSectionPlacementType {
  Static = 'Static',
  Configurable = 'Configurable'
}

export enum ReportSectionConfigurationControlType {
  Date = 'Date',
  BigNumber = 'BigNumber',
  Decimal = 'Decimal',
  Number = 'Number',
  Text = 'Text',
  Custom = 'Custom',
  Table = 'Table'
}

export enum NumericFormatType {
  Percentage = 'Percentage',
  InMillions = 'InMillions'
}

export interface ReportSectionDataSection {
  name: string;
  displayName: string;
  unit?: number;
  isSelected: boolean;
  isStatic?: boolean;
  canConfigure: boolean;
  applicableReportTypes?: ReportType[];
  subSections?: ReportSectionDataSection[];
  canReorderSubSections?: boolean;
  property?: string;
  icon?: string;
  type?: ReportSectionConfigurationControlType;
  numericFormatType?: NumericFormatType;
  groupName?: string; // when group name is specified, one item from subgroup will be required to be selected
  allowUnselectAll?: boolean;
  order?: number;
}

export interface ReportSectionNestedDataSection {
  name: string;
  nestedItemSectionConfiguration?: ReportSectionConfigurationWithOrder;
}

export interface ReportSectionConfigurationControl {
  name: string;
  displayName: string;
  applicableDataSection?: string;
  type: string;
  options?: (number | string)[];
  defaultOption?: number | string | boolean;
  maxTotalUnits?: number;
}

export interface ReportSectionConfiguration {
  typeName: string;
  name: string;
  reportSection: ReportSection;
  displayName: string;
  groupName?: string;
  applicableReportTypes: ReportType[];
  appliedReportType?: ReportType;
  placementType: ReportSectionPlacementType;
  resizable: boolean;
  minSize: number;
  maxSize: number;
  defaultSize: number;
  requirePostDataInput: boolean;
  availableDataSections: ReportSectionDataSection[];
  nestedItems: ReportSectionNestedDataSection[];
  additionalConfiguration?: AdditionalConfiguration;
  configurationControls?: ReportSectionConfigurationControl[];
  totalDataSectionsCount: number;
  selectedDataSectionsCount: number;
  reportViewModelSections: ReportViewModelSection[];
  availableDataSectionsCount?: number;
  canSelectAllDataSections?: boolean;
  maxGridUnitsAllowed?: number;
  toJSON?: () => ReportSectionConfiguration;
}

export interface ListCountConfig {
  min: number;
  max: number;
  default: number;
  value: number;
  listCountHeader: string;
}

export interface AdditionalConfiguration {
  truncationLength?: number;
  isTruncated?: boolean;
  isGraphSelected?: boolean;
  isTotalRowShown?: boolean;
  activityHistoryExternalParticipantsOnly?: boolean;
  showNotesAndCustomfieldsLatestActivityOnly?: boolean;
  listCountConfig?: ListCountConfig;
  toggleLabel?: string;
  activityDateRange?: 'ytd' | 'last12Months' | 'last3Years' | 'last5Years';
  activeInvestorsOnly?: boolean;
  chunkIndex?: number;
  chunkSize?: number;
}

export abstract class ReportSectionConfigurationBase implements ReportSectionConfiguration {
  abstract typeName: string;
  abstract name: string;
  abstract nodataLocalizationKeyName: string;
  abstract reportSection: ReportSection;
  abstract displayName: string;
  additionalConfiguration: AdditionalConfiguration = {};
  abstract applicableReportTypes: ReportType[];
  private _appliedReportType?: ReportType;
  private _totalDataSectionsCount?: number;
  canSelectAllDataSections?: boolean;
  maxGridUnitsAllowed?: number;
  totalColumnsToDisplay: number = 4;
  groupName?: string;
  nestedItems: ReportSectionNestedDataSection[] = [];
  engageActivitiesParticipantBiographyEnabled: boolean = false;

  get appliedReportType(): ReportType | undefined {
    return this._appliedReportType;
  }
  set appliedReportType(value: ReportType) {
    if (!this.applicableReportTypes.includes(value)) {
      throw new Error('Report Type not supported');
    }
    this._appliedReportType = value;
  }

  get totalDataSectionsCount(): number {
    return this.availableDataSections.length;
  }

  set totalDataSectionsCount(value: number) {
    this._totalDataSectionsCount = value;
  }

  readonly placementType: ReportSectionPlacementType = ReportSectionPlacementType.Configurable;
  get resizable(): boolean {
    return this.minSize < this.maxSize;
  }
  abstract minSize: number;
  abstract maxSize: number;
  abstract defaultSize: number;
  disabledLayoutWidthControl?: boolean;
  disableSectionSelection?: boolean;
  disableSectionReordering?: boolean;
  get requirePostDataInput(): boolean {
    return false;
  }
  abstract availableDataSections: ReportSectionDataSection[];
  configurationControls: ReportSectionConfigurationControl[] = [];
  abstract reportViewModelSections: ReportViewModelSection[];

  get selectedDataSectionsCount(): number {
    return this.availableDataSections.filter(i => i.isSelected).length;
  }

  get availableDataSectionsCount(): number {
    return this.availableDataSections.length;
  }

  toJson(): string {
    return JSON.stringify({
      name: this.name,
      reportSection: this.reportSection,
      displayName: this.displayName,
      applicableReportTypes: this.applicableReportTypes,
      appliedReportType: this.appliedReportType,
      placementType: this.placementType,
      minSize: this.minSize,
      maxSize: this.maxSize,
      defaultSize: this.defaultSize,
      disabledLayoutWidthControl: this.disabledLayoutWidthControl,
      disableSectionSelection: this.disableSectionSelection,
      disableSectionReordering: this.disableSectionReordering,
      availableDataSections: this.availableDataSections,
      additionalConfiguration: this.additionalConfiguration,
      configurationControls: this.configurationControls,
      reportViewModelSections: this.reportViewModelSections,
      selectedDataSectionsCount: this.selectedDataSectionsCount,
      totalDataSectionsCount: this.totalDataSectionsCount,
      canSelectAllDataSections: this.canSelectAllDataSections ?? true,
      maxGridUnitsAllowed: this.maxGridUnitsAllowed,
      nodataLocalizationKeyName: this.nodataLocalizationKeyName,
      totalColumnsToDisplay: this.totalColumnsToDisplay,
      nestedItems: this.nestedItems.map(section => ({
        name: section.name,
        nestedItemSectionConfiguration: {
          order: section?.nestedItemSectionConfiguration?.order,
          isSelected: section?.nestedItemSectionConfiguration?.isSelected,
          configClass: section?.nestedItemSectionConfiguration?.config?.typeName,
          config: section?.nestedItemSectionConfiguration?.config?.toJson(),
          width: section?.nestedItemSectionConfiguration?.width,
          appliedReportType: section?.nestedItemSectionConfiguration?.config?.appliedReportType
        }
      }))
    });
  }

  equals(other: ReportSectionConfigurationBase): boolean {
    return (
      this.name === other.name &&
      this.reportSection === other.reportSection &&
      this.displayName === other.displayName &&
      isEqual(this.applicableReportTypes, other.applicableReportTypes) &&
      this.appliedReportType === other.appliedReportType &&
      this.placementType === other.placementType &&
      this.minSize === other.minSize &&
      this.maxSize === other.maxSize &&
      this.defaultSize === other.defaultSize &&
      this.disabledLayoutWidthControl === other.disabledLayoutWidthControl &&
      this.disableSectionSelection === other.disableSectionSelection &&
      isEqual(this.availableDataSections, other.availableDataSections) &&
      isEqual(this.configurationControls, other.configurationControls) &&
      isEqual(this.additionalConfiguration, other.additionalConfiguration) &&
      isEqual(this.reportViewModelSections, other.reportViewModelSections)
    );
  }
}

export interface ReportSectionConfigurationWithOrder {
  order: number;
  isSelected: boolean;
  isActiveTemplate: boolean;
  width?: number;
  configClass: new () => ReportSectionConfigurationBase;
  config: ReportSectionConfigurationBase;
  equals(other: ReportSectionConfigurationWithOrder): boolean;
}

export class ReportSectionConfigurationWithOrderImpl implements ReportSectionConfigurationWithOrder {
  order: number;
  isSelected: boolean;
  isActiveTemplate: boolean;
  configClass: new () => ReportSectionConfigurationBase;
  config: ReportSectionConfigurationBase;
  width: number;
  equals(other: ReportSectionConfigurationWithOrder): boolean {
    return (
      this.order === other.order &&
      this.isSelected === other.isSelected &&
      this.isActiveTemplate === other.isActiveTemplate &&
      this.configClass === other.configClass &&
      this.config.equals(other.config) &&
      this.width === other.width
    );
  }

  constructor(order: number, isSelected: boolean, configClass: new () => ReportSectionConfigurationBase) {
    this.order = order;
    this.isSelected = isSelected;
    this.isActiveTemplate = false;
    this.configClass = configClass;
    this.config = new configClass();
    this.width = this.config.defaultSize;
  }
}

export function serializeReportSections(data: ReportSectionConfigurationWithOrder[]): string {
  return JSON.stringify(
    data.map(section => {
      return {
        order: section.order,
        isSelected: section.isSelected,
        configClass: section.config.typeName,
        config: section.config.toJson(),
        width: section.width,
        appliedReportType: section.config.appliedReportType
      };
    })
  );
}
